<template>
  <v-container>
    <v-card class="mx-auto mt-5" max-width="800">
      <v-toolbar color="primary" flat>
        <v-btn icon @click="goBack">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title>{{ $t('Tests von') }}: {{ userName }}</v-toolbar-title>
      </v-toolbar>
      <v-list v-if="sessions && sessions.length > 0">
        <v-list-item
          v-for="session in sessions"
          :key="session._id"
          @click="selectSession(session)"
          link
        >
          <v-list-item-header>
            <v-list-item-title>
              {{ session.title || session.caseTitle || session.object || 'Unbekannte Prüfung' }}
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ formatDate(session._created) }} - {{ session.type || 'Prüfung' }}
            </v-list-item-subtitle>
          </v-list-item-header>
          <template v-slot:append>
            <v-chip :color="getStatusColor(session)" size="small" class="mr-2">
              {{ getStatusText(session) }}
            </v-chip>
            <v-icon>mdi-chevron-right</v-icon>
          </template>
        </v-list-item>
      </v-list>
      <v-card-text v-else-if="loading" class="text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-card-text>
      <v-card-text v-else class="text-center">
        {{ $t('Keine Tests für diesen Azubi gefunden.') }}
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import APIServiceRestDB from '@/services/MesskursBackendService';
import APIService from '@/services/APIService';

export default {
  name: 'EvaluationUserTestsView',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const sessions = ref([]);
    const loading = ref(false);
    const userName = ref("");
    const userId = route.params.userId;

    const fetchSessions = async () => {
      loading.value = true;
      try {
        // Fetch specific user data. Do not block evaluation if profile API denies access.
        const user = await APIService.getUser(userId).catch((error) => {
          console.warn("Could not load user profile for evaluation:", userId, error?.response?.status || error?.message || error);
          return null;
        });
        if (user) {
          userName.value = user.name;
        } else if (!userName.value) {
          userName.value = userId;
        }

        const result = await APIServiceRestDB.getSessionsForUser(userId);
        const safeResult = Array.isArray(result) ? result : [];
        // Enrich sessions with case titles if possible
        for(let session of safeResult) {
            if(session.object) {
                const obj = await APIServiceRestDB.getObjectWithId(session.object);
                if(obj) {
                    session.caseTitle = obj.title;
                    session.type = obj.type;
                }
            }
        }
        sessions.value = safeResult;
      } catch (error) {
        console.error("Error fetching sessions for user:", error);
      } finally {
        loading.value = false;
      }
    };

    const selectSession = async (session) => {
      // Set the session in the store before navigating
      await store.dispatch('updateSession', session);
      await store.dispatch('updateSessionId', session._id);
      
      router.push({ 
        name: 'EvaluationDetail', 
        params: { userId: userId, sessionId: session._id },
        query: { case: session.object, session: session._id }
      });
    };

    const goBack = () => {
      router.push({ name: 'EvaluationList' });
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleString();
    };

    const getStatusColor = (session) => {
      if (session.testFinished) return 'green';
      if (session.status === 'active') return 'blue';
      return 'orange';
    };

    const getStatusText = (session) => {
      if (session.testFinished) return 'Abgeschlossen';
      if (session.status === 'active') return 'In Bearbeitung';
      return 'Offen';
    };

    onMounted(() => {
      fetchSessions();
    });

    return {
      sessions,
      loading,
      userName,
      selectSession,
      goBack,
      formatDate,
      getStatusColor,
      getStatusText
    };
  }
};
</script>
